import { useEffect, useRef, useState } from "react";
import {
  RichTextInput,
  RichTextInputToolbar,
  FormatButtons,
  ColorButtons,
  AlignmentButtons,
  ListButtons,
  LinkButtons,
  QuoteButtons,
  ClearButtons,
  useTiptapEditor,
} from "ra-input-rich-text";
import { ToggleButton, Tooltip } from "@mui/material";
import { Image as ImageIcon } from "@mui/icons-material";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";
import { toAbsoluteImageUrls, toRelativeImageUrls } from "./imageUrls";

const ACCEPT = "image/png,image/jpeg,image/gif,image/webp";

/**
 * Toolbar control that uploads an image (Feature 4317 / N7) and inserts it into
 * the body. Three ways in: the toolbar button (file picker), pasting a
 * screenshot, or dragging a file onto the editor. Uploads go to
 * POST /help-articles/images (SuperAdmin); the returned URL is embedded as an
 * absolute API URL so it renders in the Help Center.
 */
const ImageUploadControl = () => {
  const editor = useTiptapEditor();
  const notifyError = useRichApiErrorNotify();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const uploadAndInsert = async (file: File) => {
    if (!editor || !file.type.startsWith("image/")) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { json } = await httpClient(`${getPartnerAwareApiUrl()}/help-articles/images`, {
        method: "POST",
        body: form,
      });
      const path: string | undefined = json?.url ?? json?.data?.url;
      if (!path) throw new Error("missing url");
      editor
        .chain()
        .focus()
        .insertContent({ type: "image", attrs: { src: `${getPartnerAwareApiUrl()}${path}` } })
        .run();
    } catch (error) {
      notifyError(error);
    } finally {
      setBusy(false);
    }
  };

  // Paste / drag-drop of image files, bound to the editor's DOM node.
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom as HTMLElement;
    const firstImage = (list?: FileList | null) => Array.from(list ?? []).find((f) => f.type.startsWith("image/"));

    const onPaste = (e: ClipboardEvent) => {
      const file = firstImage(e.clipboardData?.files);
      if (file) {
        e.preventDefault();
        void uploadAndInsert(file);
      }
    };
    const onDrop = (e: DragEvent) => {
      const file = firstImage(e.dataTransfer?.files);
      if (file) {
        e.preventDefault();
        void uploadAndInsert(file);
      }
    };
    dom.addEventListener("paste", onPaste);
    dom.addEventListener("drop", onDrop);
    return () => {
      dom.removeEventListener("paste", onPaste);
      dom.removeEventListener("drop", onDrop);
    };
  }, [editor]);

  return (
    <>
      <Tooltip title="Insert image — upload, paste, or drag a file in">
        <span>
          <ToggleButton
            value="upload-image"
            size="small"
            disabled={busy || !editor}
            selected={false}
            onClick={() => inputRef.current?.click()}
          >
            <ImageIcon fontSize="small" />
          </ToggleButton>
        </span>
      </Tooltip>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void uploadAndInsert(f);
          e.target.value = "";
        }}
      />
    </>
  );
};

/** Rich-text body input with screenshot upload (paste / drag / pick). */
export const ArticleBodyInput = () => (
  <RichTextInput
    source="body_html"
    label="Body"
    fullWidth
    format={(v?: string) => toAbsoluteImageUrls(v, getPartnerAwareApiUrl())}
    parse={(v?: string) => toRelativeImageUrls(v)}
    sx={{ "& .ProseMirror": { minHeight: 280 } }}
    toolbar={
      <RichTextInputToolbar>
        <FormatButtons />
        <ColorButtons />
        <AlignmentButtons />
        <ListButtons />
        <LinkButtons />
        <QuoteButtons />
        <ImageUploadControl />
        <ClearButtons />
      </RichTextInputToolbar>
    }
  />
);
